import React, { useEffect, useMemo, useRef } from 'react';
import { View, Text, Pressable, Platform, Animated, Easing } from 'react-native';
import { List, BarChart2, Zap } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { getMatchCardStyles } from './MatchCard.styles';
import { MatchCardHeroShell } from './MatchCardHeroShell';
import { MatchCardPickRow } from './MatchCardPickRow';
import { MatchCardLive } from './MatchCardLive';
import { MatchCardFinished } from './MatchCardFinished';
import {
  formatKickoffClock,
  formatMatchStageBadgeLabel,
  resolvePrediction,
  isCorrectOutcome,
} from '../../../src/worldcup/helpers/worldcup.helper';
import { GROUP_STAGE_FRONTEND_KEYS } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import { formatWorldCupOrdinalRank } from '../../../src/worldcup/helpers/worldCupGroupsDisplay.helper';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import type { MatchTeamKit, WorldCupMatch } from '../../../src/worldcup/types/worldcup.types';

/**
 * Shape `getTeamKitConfig` for one side: the server's colours when it sent them, otherwise the
 * team name, which is the name-map lookup every caller used before `homeKit`/`awayKit` were
 * populated (AC-3.8). The payload names the fields `primary`/`secondary`/`type`; the lookup
 * takes the DB column names, so the two are bridged here rather than at each call site.
 */
function toKitLookupTeam(
  name: string,
  kit: MatchTeamKit | null | undefined,
): string | { name: string; kitPrimary: string; kitSecondary: string; kitType: string | null } {
  if (!kit) return name;
  return { name, kitPrimary: kit.primary, kitSecondary: kit.secondary, kitType: kit.type ?? null };
}

interface MatchCardProps {
  match: WorldCupMatch;
  predictionInput?: { home: string; away: string };
  onPredictionChange?: (matchId: string, home: string, away: string) => void;
  isBoosted?: boolean;
  isBoostLocked?: boolean;
  /**
   * Whether the x2 may be placed on this match's stage. False from the Round of 16 onward
   * (Group Stage + Round of 32 only). When false the button renders greyed and disabled.
   */
  isBoostEligible?: boolean;
  onToggleBoost?: (matchId: string) => void;
  onFixturePress?: (matchId: string) => void;
  onStatsPress?: (matchId: string) => void;
  /** Welcome rules tutorial: use full column width so previews are not cropped on small screens */
  layout?: 'default' | 'tutorial';
  /** Override the "You:" label in finished/live prediction footers. Defaults to "You". */
  predictionLabel?: string;
  /** Late joiner forfeited this (finished) match — show a "Joined mid-tournament" marker, no pick. */
  isMissedPreJoin?: boolean;
  /**
   * Flags (national teams) or kits (clubs). Defaults to flags, i.e. the World Cup, so every
   * existing caller is unchanged (AC-3.8). Same prop and same default as the leaderboard's
   * `teamVisual` (AC-3.6) — a club competition drawing one mark per team must draw the kit,
   * because the country flag makes every Spanish club look like Spain.
   */
  teamVisual?: 'flag' | 'kit';
}

export const MatchCard: React.FC<MatchCardProps> = ({
  match,
  predictionInput,
  onPredictionChange,
  isBoosted = false,
  isBoostLocked = false,
  isBoostEligible = true,
  onToggleBoost,
  onFixturePress,
  onStatsPress,
  layout = 'default',
  predictionLabel,
  isMissedPreJoin = false,
  teamVisual = 'flag',
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const styles = useMemo(
    () => getMatchCardStyles(theme, themeMode, { layout }),
    [layout, theme, themeMode],
  );
  const canEditPick = match.isPickEditable !== false && onPredictionChange != null;
  const isMaskedMonkeyPick = match.prediction?.isMonkeyPick === true && match.isPickEditable !== false && predictionInput == null;
  const prediction = isMaskedMonkeyPick
    ? { home: '', away: '' }
    : resolvePrediction(predictionInput, match.prediction);
  const kickoffClock = formatKickoffClock(match.kickoffTime);
  const isGroupStage = GROUP_STAGE_FRONTEND_KEYS.includes(match.stageKey);
  const stageBadgeText = isGroupStage && match.groupLetter
    ? `${formatMatchStageBadgeLabel(match)} · Group ${match.groupLetter}`
    : formatMatchStageBadgeLabel(match);
  const homeRank = match.homeGroupPosition != null ? formatWorldCupOrdinalRank(match.homeGroupPosition) : null;
  const awayRank = match.awayGroupPosition != null ? formatWorldCupOrdinalRank(match.awayGroupPosition) : null;
  const isFinished = match.status === 'finished';
  const isLive = match.status === 'live';
  const canBoost = isBoostEligible && !isFinished && !isLive && !isBoostLocked && onToggleBoost != null;
  const boostAnim = useRef(new Animated.Value(0)).current;
  const liveAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!isBoosted) {
      boostAnim.setValue(0);
      return;
    }
    Animated.timing(boostAnim, {
      toValue: 1,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [boostAnim, isBoosted]);

  useEffect(() => {
    if (!isLive) return;
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(liveAnim, { toValue: 0.2, duration: 700, useNativeDriver: true }),
        Animated.timing(liveAnim, { toValue: 1, duration: 700, useNativeDriver: true }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [isLive, liveAnim]);

  const cardScale = {
    transform: [{ scale: boostAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.02] }) }],
  };

  // Prefer the colours the server sent for this club; fall back to the name lookup, which is
  // what every caller relied on before `homeKit`/`awayKit` were populated (AC-3.8).
  const homeKitFallback = useMemo(
    () => getTeamKitConfig(toKitLookupTeam(match.homeTeam, match.homeKit)),
    [match.homeKit, match.homeTeam],
  );
  const awayKitFallback = useMemo(
    () => getTeamKitConfig(toKitLookupTeam(match.awayTeam, match.awayKit)),
    [match.awayKit, match.awayTeam],
  );
  // `CountryFlag` shows its kit fallback exactly when it has no flag to draw, so kit mode is
  // expressed by withholding the code rather than by a second rendering path (AC-3.8) — the
  // same lever the leaderboard chip pulls for AC-3.6.
  const homeFlagCode = teamVisual === 'kit' ? '' : match.homeCountryCode;
  const awayFlagCode = teamVisual === 'kit' ? '' : match.awayCountryCode;

  const hasLiveScore = isLive && match.homeScore != null && match.awayScore != null;
  const hasFinishedScore = isFinished && match.homeScore != null && match.awayScore != null;
  // Competition picks are scored on the 90-minute result (PRD §4). For a knockout decided in extra
  // time the final (`homeScore`, incl. ET) differs from the regulation score, so judge correctness
  // on `fullTime*` when present — matching the server — and fall back to the final otherwise.
  const scoredHome = match.fullTimeHomeScore ?? match.homeScore;
  const scoredAway = match.fullTimeAwayScore ?? match.awayScore;
  const hasPrediction =
    match.prediction != null &&
    match.prediction.home != null &&
    match.prediction.away != null;
  const isExactHit =
    hasFinishedScore &&
    hasPrediction &&
    match.prediction!.home === scoredHome &&
    match.prediction!.away === scoredAway;
  const isCorrectResult =
    hasFinishedScore &&
    hasPrediction &&
    !isExactHit &&
    isCorrectOutcome(scoredHome!, scoredAway!, match.prediction!.home, match.prediction!.away);
  // The server scores picks with stage-weighted points (4/2 … 20/12) and the golden-ball ×2,
  // writing the final value to `pointsEarned` at settlement. Trust ONLY that value — never
  // fabricate a local estimate (the old 5/2 defaults mis-showed e.g. a group exact as +5 when
  // it scores +4). `pointsEarned` is null strictly until settlement; a settled miss is written
  // as 0. So a finished pick still null means "scoring…" in progress, not zero.
  const isPointsPending = isFinished && hasPrediction && match.prediction?.pointsEarned == null;
  const earnedPoints = match.prediction?.pointsEarned ?? 0;

  // Judge the LIVE badge on the 90-minute result (`scoredHome/Away`), matching the finished
  // card and the server (US-9 / PRD §4). A knockout tied at 90' that then scores in extra time
  // stays `live`; judging on the running score would flip a correct 90-min pick to a miss during
  // ET, then back at settlement. The card still DISPLAYS the true running score below.
  const isLiveExactHit =
    hasLiveScore && hasPrediction &&
    match.prediction!.home === scoredHome &&
    match.prediction!.away === scoredAway;
  const isLiveCorrectResult =
    hasLiveScore && hasPrediction && !isLiveExactHit &&
    isCorrectOutcome(scoredHome!, scoredAway!, match.prediction!.home, match.prediction!.away);
  // Show a "90'" cue next to the live badge only when the judged (90-min) score diverges from the
  // running scoreline — i.e. a knockout with extra-time goals. Equal scores (all group matches,
  // and knockouts before any ET goal) need no cue.
  const isLive90MinJudged =
    isLive && hasLiveScore && (scoredHome !== match.homeScore || scoredAway !== match.awayScore);

  if (isLive) {
    return (
      <MatchCardLive
        match={match}
        isBoosted={isBoosted}
        stageBadgeText={stageBadgeText}
        homeRank={homeRank}
        awayRank={awayRank}
        cardScale={cardScale}
        liveAnim={liveAnim}
        isLiveExactHit={isLiveExactHit}
        isLiveCorrectResult={isLiveCorrectResult}
        isLive90MinJudged={isLive90MinJudged}
        hasPrediction={hasPrediction}
        hasLiveScore={hasLiveScore}
        styles={styles}
        theme={theme}
        predictionLabel={predictionLabel}
        onFixturePress={onFixturePress}
        onStatsPress={onStatsPress}
      />
    );
  }

  if (isFinished) {
    return (
      <MatchCardFinished
        match={match}
        isBoosted={isBoosted}
        stageBadgeText={stageBadgeText}
        homeRank={homeRank}
        awayRank={awayRank}
        cardScale={cardScale}
        isExactHit={isExactHit}
        isCorrectResult={isCorrectResult}
        hasPrediction={hasPrediction}
        earnedPoints={earnedPoints}
        isPointsPending={isPointsPending}
        isMissedPreJoin={isMissedPreJoin}
        styles={styles}
        theme={theme}
        predictionLabel={predictionLabel}
        onFixturePress={onFixturePress}
        onStatsPress={onStatsPress}
      />
    );
  }

  return (
    <Animated.View style={cardScale}>
      <View style={[styles.card, isBoosted && styles.cardBoosted]}>
        <MatchCardHeroShell />

        <View style={styles.cardContent}>
          <View style={styles.cardTopBar}>
            <View style={styles.cardTopBarSpacer} />
            <View style={styles.stageBadgeOnPurple}>
              <Text style={styles.stageBadgeOnPurpleText}>{stageBadgeText} · {kickoffClock}</Text>
            </View>
            <View style={styles.cardTopBarRight}>
              {onToggleBoost != null && (
                <Pressable
                    disabled={!canBoost}
                    onPress={() => onToggleBoost(match.id)}
                    style={({ pressed, hovered }) => [
                      styles.boostButton,
                      isBoosted && styles.boostButtonActive,
                      !canBoost && styles.boostButtonDisabled,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && canBoost && styles.boostButtonPressed,
                      hovered && Platform.OS === 'web' && canBoost && styles.boostButtonHover,
                    ]}
                  >
                    <Zap
                      size={9}
                      color={isBoosted ? theme.textOnCard : !canBoost ? 'rgba(255,255,255,0.2)' : theme.accent}
                      fill={isBoosted ? theme.textOnCard : 'transparent'}
                      strokeWidth={2.5}
                    />
                    <Text style={[styles.boostX2Multiply, isBoosted && styles.boostX2MultiplyActive, !canBoost && styles.boostX2Disabled]}>×</Text>
                    <Text style={[styles.boostX2Digit, isBoosted && styles.boostX2DigitActive, !canBoost && styles.boostX2Disabled]}>2</Text>
                </Pressable>
              )}
            </View>
          </View>

          <View style={styles.matchRow}>
              <View style={styles.teamBlock}>
                <View style={styles.flagWrapper}>
                  <CountryFlag
                    countryCode={homeFlagCode}
                    kitFallback={homeKitFallback}
                    accessibilityLabel={`${match.homeTeam} ${teamVisual}`}
                  />
                </View>
                <Text style={styles.teamName} numberOfLines={1} ellipsizeMode="tail">
                  {match.homeTeam}
                </Text>
                {homeRank != null && <Text style={styles.groupPosition}>{homeRank}</Text>}
              </View>

              <MatchCardPickRow
                matchId={match.id}
                homeTeam={match.homeTeam}
                awayTeam={match.awayTeam}
                prediction={prediction}
                onPredictionChange={canEditPick ? onPredictionChange : undefined}
                showMonkeyBadge={match.prediction?.isMonkeyPick === true && match.isPickEditable === false}
              />

              <View style={styles.teamBlock}>
                <View style={styles.flagWrapper}>
                  <CountryFlag
                    countryCode={awayFlagCode}
                    kitFallback={awayKitFallback}
                    accessibilityLabel={`${match.awayTeam} ${teamVisual}`}
                  />
                </View>
                <Text style={styles.teamName} numberOfLines={1} ellipsizeMode="tail">
                  {match.awayTeam}
                </Text>
                {awayRank != null && <Text style={styles.groupPosition}>{awayRank}</Text>}
              </View>
            </View>

          <View style={styles.bottomRow}>
            <Pressable
              onPress={() => onStatsPress?.(match.id)}
              style={({ pressed }) => [
                styles.cardActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.cardActionButtonPressed,
              ]}
            >
              <BarChart2 size={13} color={styles.cardActionIcon.color as string} strokeWidth={2} />
              <Text style={styles.cardActionText}>Stats</Text>
            </Pressable>

            <View style={styles.cardActionDivider} />

            <Pressable
              onPress={() => onFixturePress?.(match.id)}
              style={({ pressed }) => [
                styles.cardActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.cardActionButtonPressed,
              ]}
            >
              <List size={13} color={styles.cardActionIcon.color as string} strokeWidth={2} />
              <Text style={styles.cardActionText}>Match Details</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};
